/**
 * Sidebar — role-aware, contextual workspace navigation.
 *
 * Two separate role experiences:
 *
 *  ADMIN (multi-tenant operator) — three tiers, one visible at a time:
 *    Tier 0, Global rail (persistent): top-level destinations
 *    Tier 1, Borrower workspace (in a borrower/book): KYC header + BOOK sections + FACILITIES
 *    Tier 2, Deal or Facility workspace: that object's sections (replaces tier 1)
 *
 *  BORROWER (single-tenant) — flat portal, no multi-tenant chrome:
 *    Rail by permission: Deals, Facilities, Book, Upload, Team. Inside their book → curated
 *    book sections. Inside a facility they hold → the reading of the facility
 *    (NON_ADMIN_FACILITY_NAV), never its configuration or its files, and never the
 *    write controls inside a section they do read.
 *
 * Borrower context is bound to the URL scope, never to the persisted store alone
 * (otherwise a previously-visited borrower leaks onto the landing).
 */

import { Link, useLocation } from "react-router-dom";
import cn from "classnames";
import { useEffect, useState } from "react";
import { useFacilityStore } from "@/stores/useFacilityStore";
import { usePortfolioStore } from "@/stores/usePortfolioStore";
import { useGlobalStore } from "@/stores/useGlobalStore";
import { useBorrowerStore } from "@/stores/useBorrowerStore";
import { usePermissions, hasDealsAccess, isAnonymous, type UserPermissions } from "@/hooks/usePermissions";
import { api } from "@/lib/api";
import { ANON_FACILITY_NAV, FACILITY_NAV, NON_ADMIN_FACILITY_NAV, resolveSection } from "@/pages/facility/facility.constants";
import { ANON_DEAL_NAV, DEAL_NAV, NON_ADMIN_DEAL_NAV, resolveDealSection } from "@/pages/deal/deal.constants";
import type { FacilityInfo } from "@/pages/facility/facility.types";
import s from "../sidebar.module.sass";
import { BorrowerHeader } from "./BorrowerHeader";
import { BookGroup } from "./BookGroup";
import { CollapsibleGroup } from "./CollapsibleGroup";
import { NAV_ICON, SECTION_ICON } from "./sidebar.icons";
import type { BorrowerCtx, SidebarBorrower, SidebarDeal, SidebarFacility, SidebarMetrics } from "./types";

type Scope = "deal" | "facility" | "workspace" | "global";

/**
 * Map a standalone facility sub-route (/facility/:id/<seg>) to the cockpit section it
 * belongs to, or null when it is no section at all. These routes carry no
 * ?section=, so resolveSection's "overview" fallback would falsely highlight
 * Overview while the user sits on a different page.
 */
function subRouteSection(seg: string): string | null {
  // `setup` only redirects to config (App.tsx).
  const id = seg === "setup" ? "config" : seg;
  return FACILITY_NAV.items.some((i) => i.id === id) ? id : null;
}


export function Sidebar({ collapsed = false }: { collapsed?: boolean }) {
  const location = useLocation();
  const perms = usePermissions();

  const facilityId = matchFacilityId(location.pathname);
  const dealId = matchDealId(location.pathname);
  const urlBorrowerId = matchBorrowerId(location.pathname);
  const urlBookId = matchBookId(location.pathname);

  const sbBorrower = useBorrowerStore((st) => st.borrower);
  const sbBooks = useBorrowerStore((st) => st.books);
  const sbFacilities = useBorrowerStore((st) => st.facilities);
  const sbDeals = useBorrowerStore((st) => st.deals);

  const facility = useFacilityStore((st) => st.facility);
  const metrics = usePortfolioStore((st) => st.metrics) as SidebarMetrics;
  const alertCount = useGlobalStore((st) => st.unacknowledgedAlertCount);
  const incidentCount = useGlobalStore((st) => st.activeIncidentCount);
  const borrowerCount = useGlobalStore((st) => st.borrowerCount);
  const applicationNewCount = useGlobalStore((st) => st.applicationNewCount);

  // Admin drill-down: populate the borrower store when on a facility URL.
  // (Book pages load the borrower store themselves; borrower-detail pages too.)
  useEffect(() => {
    if (!facilityId || !perms.isAdmin) return;
    if (facility?.borrower?.id) {
      useBorrowerStore.getState().load(facility.borrower.id);
    } else {
      api.getFacility(facilityId).then((d: FacilityInfo) => {
        if (d?.borrower?.id) useBorrowerStore.getState().load(d.borrower.id);
      }).catch(() => {});
    }
  }, [facilityId, facility?.borrower?.id, perms.isAdmin]);

  const isAdmin = perms.isAdmin;
  const canBook = isAdmin || perms.bookView;
  const scope: Scope =
    facilityId ? "facility"
      // The contract's cockpit is a tier of its own, above the facility's: an
      // investor holds the Deal, so the Deal is what they navigate.
      : dealId ? "deal"
      : (urlBookId || urlBorrowerId) ? "workspace" : "global";

  // Outside an object route the persisted store is ignored so a stale borrower can't leak.
  const borrowerCtx =
    scope === "global" ? null : buildBorrowerCtx({ sbBorrower, sbBooks, sbFacilities, facilityId, facility, urlBorrowerId });

  const effectiveBookId = urlBookId ?? borrowerCtx?.activeBook?.id ?? perms.bookId ?? null;

  const searchParams = new URLSearchParams(location.search);
  // On /facility/:id the section lives in ?section=; resolving legacy ids keeps the
  // highlight on the section FacilityPage redirects to. Sub-routes (/config, /setup)
  // carry it in the path; a non-section path highlights nothing instead of Overview.
  // Links stay clickable either way: a DRAFT facility has portfolio data to preview.
  const facilitySubRoute = location.pathname.match(/^\/facility\/[^/]+\/([^/]+)/)?.[1] ?? null;
  const activeSection = dealId
    // Deal sections have their own ids (`payment-report`); the facility resolver
    // would map them to Overview.
    ? resolveDealSection(searchParams.get("section"))
    : facilitySubRoute
      ? subRouteSection(facilitySubRoute)
      : resolveSection(searchParams.get("section"));
  const bookTab = searchParams.get("tab") || "overview";

  return (
    <nav className={s.nav}>
      {/* Tier 0: global rail */}
      <GlobalZone
        perms={perms}
        pathname={location.pathname}
        alertCount={alertCount}
        incidentCount={incidentCount}
        borrowerCount={borrowerCount}
        applicationNewCount={applicationNewCount}
        hasFacilities={hasDealsAccess(perms)}
        collapsed={collapsed}
      />

      {/* Tier 2: Deal workspace. Admin sees every section; a contract grant
           opens the reading, not the terms Debita keys; an anonymous grant loses
           Cash, whose statement lines name the holder. */}
      {!collapsed && scope === "deal" && dealId && (
        <DealContext
          dealId={dealId}
          isAdmin={isAdmin}
          anonymous={perms.anonymized || perms.anonymizedBookIds.length > 0}
          activeSection={activeSection}
        />
      )}

      {/* Tier 2: Facility workspace. Admin sees the full nav; others see the
           sections their facility grant opens. */}
      {!collapsed && scope === "facility" && facilityId && (
        <FacilityContext
          facilityId={facilityId}
          facilityName={facility?.facility_name ?? "Facility"}
          borrower={borrowerCtx?.borrower ?? null}
          bookId={borrowerCtx?.activeBook?.id ?? null}
          isAdmin={isAdmin}
          anonymous={isAnonymous(perms, facility?.book_id ?? null)}
          activeSection={activeSection}
          covenantStatus={metrics?.covenant_status ?? null}
        />
      )}

      {/* Tier 1: borrower workspace (book + facilities) */}
      {!collapsed && scope === "workspace" && (
        <div className={s.sectionDivider}>
          {borrowerCtx && <BorrowerHeader borrower={borrowerCtx.borrower} activeBook={borrowerCtx.activeBook} />}

          {canBook && effectiveBookId && (
            <BookGroup
              bookId={effectiveBookId}
              currentBookId={urlBookId}
              activeTab={bookTab}
              isAdmin={isAdmin}
              defaultOpen
            />
          )}

          {/* Facilities are part of the admin's borrower workspace. A borrower reaches
              their facilities through the flat rail, never as a facility sub-tree here. */}
          {isAdmin && borrowerCtx && (
            <FacilitiesList
              facilities={borrowerCtx.facilities}
              deals={sbDeals}
              borrowerId={borrowerCtx.borrower.id}
            />
          )}

          {isAdmin && effectiveBookId && (
            <Link
              to={`/books/${effectiveBookId}/team`}
              className={cn(s.sectionNavLink, { [s.active]: location.pathname === `/books/${effectiveBookId}/team` })}
            >
              {SECTION_ICON.team}Team Access
            </Link>
          )}

          {/* Borrower record (KYC / danger zone), reachable after the book redirect. */}
          {isAdmin && borrowerCtx && (
            <Link to={`/borrowers/${borrowerCtx.borrower.id}?manage=1`} className={s.sectionNavLink}>
              {SECTION_ICON.settings}Borrower settings
            </Link>
          )}
        </div>
      )}
    </nav>
  );
}

function GlobalZone({ perms, pathname, alertCount, incidentCount, borrowerCount, applicationNewCount, hasFacilities, collapsed }: {
  perms: UserPermissions;
  pathname: string;
  alertCount: number;
  incidentCount: number;
  borrowerCount: number;
  applicationNewCount: number;
  hasFacilities: boolean;
  collapsed: boolean;
}) {
  if (perms.isAdmin) {
    // The rail reflects the top-level you're under, even when deep:
    //   a facility sits under Facilities; a book/borrower sits under Borrowers.
    const inFacilities = pathname === "/" || pathname.startsWith("/facility");
    // `/deals` (the index) and `/deal/:id` (one contract) both light this one.
    const inDeals = pathname.startsWith("/deal");
    const inBorrowers = pathname.startsWith("/borrowers") || pathname.startsWith("/books");
    return (
      <div>
        <Link to="/overview" className={cn(s.navItem, { [s.active]: pathname === "/overview" })} title={collapsed ? "Overview" : undefined}>
          <span className={s.navLabel}>{NAV_ICON.overview}{!collapsed && <span>Overview</span>}</span>
        </Link>
        {/* Contracts above mechanisms, which is the hierarchy: a facility only
            means something under a Deal. */}
        <Link to="/deals" className={cn(s.navItem, { [s.active]: inDeals })} title={collapsed ? "Deals" : undefined}>
          <span className={s.navLabel}>{NAV_ICON.deals}{!collapsed && <span>Deals</span>}</span>
        </Link>
        <Link to="/" className={cn(s.navItem, { [s.active]: inFacilities })} title={collapsed ? "Facilities" : undefined}>
          <span className={s.navLabel}>{NAV_ICON.facilities}{!collapsed && <span>Facilities</span>}</span>
        </Link>
        <Link to="/borrowers" className={cn(s.navItem, { [s.active]: inBorrowers })} title={collapsed ? "Borrowers" : undefined}>
          <span className={s.navLabel}>{NAV_ICON.borrowers}{!collapsed && <span>Borrowers{borrowerCount > 0 ? ` (${borrowerCount})` : ""}</span>}</span>
        </Link>
        <Link
          to="/incidents"
          className={cn(s.navItem, { [s.active]: pathname === "/incidents" })}
          title={collapsed ? "Incidents" : undefined}
        >
          <span className={s.navLabel}>{NAV_ICON.incidents}{!collapsed && <span>Incidents</span>}</span>
          {incidentCount > 0 && (collapsed ? <span className={s.badgeDot} /> : <span className={s.badge}>{incidentCount}</span>)}
        </Link>
        <Link
          to="/applications"
          className={cn(s.navItem, { [s.active]: pathname === "/applications" })}
          title={collapsed ? "Applications" : undefined}
        >
          <span className={s.navLabel}>{NAV_ICON.applications}{!collapsed && <span>Applications</span>}</span>
          {applicationNewCount > 0 && (collapsed ? <span className={s.badgeDot} /> : <span className={s.badge}>{applicationNewCount}</span>)}
        </Link>
        <Link
          to="/alerts"
          className={cn(s.navItem, { [s.active]: pathname === "/alerts" })}
          title={collapsed ? "Alerts" : undefined}
        >
          <span className={s.navLabel}>{NAV_ICON.alerts}{!collapsed && <span>Alerts</span>}</span>
          {alertCount > 0 && (collapsed ? <span className={s.badgeDot} /> : <span className={s.badge}>{alertCount}</span>)}
        </Link>
        <Link
          to="/admin/system"
          className={cn(s.navItem, { [s.active]: pathname === "/admin/system" })}
          title={collapsed ? "System" : undefined}
        >
          <span className={s.navLabel}>{NAV_ICON.settings}{!collapsed && <span>System</span>}</span>
        </Link>
      </div>
    );
  }

  // Borrower — flat permitted destinations, no multi-tenant chrome.
  const inDeals = pathname.startsWith("/deal");
  return (
    <div>
      {/* Deals first: covenants, waterfall and cash are read on the contract; the
          facility keeps its pool and collateral. Without this entry a grant on a
          Deal could reach a facility but never the contract above it. */}
      {hasFacilities && (
        <Link to="/deals" className={cn(s.navItem, { [s.active]: inDeals })} title={collapsed ? "Deals" : undefined}>
          <span className={s.navLabel}>{NAV_ICON.deals}{!collapsed && <span>Deals</span>}</span>
        </Link>
      )}
      {hasFacilities && (
        <Link to="/" className={cn(s.navItem, { [s.active]: pathname === "/" })} title={collapsed ? "Facilities" : undefined}>
          <span className={s.navLabel}>{NAV_ICON.facilities}{!collapsed && <span>Facilities</span>}</span>
        </Link>
      )}
      {perms.books.filter((b) => b.bookView).map((b) => (
        <Link
          key={b.bookId}
          to={`/books/${b.bookId}`}
          className={cn(s.navItem, { [s.active]: pathname === `/books/${b.bookId}` || pathname.startsWith(`/books/${b.bookId}/`) })}
          title={collapsed ? (perms.books.length > 1 ? b.bookName ?? "Book" : "Book") : undefined}
        >
          <span className={s.navLabel}>
            {NAV_ICON.book}
            {!collapsed && <span>{perms.books.length > 1 ? b.bookName ?? "Book" : "Book"}</span>}
          </span>
        </Link>
      ))}
      {perms.bookUpload && (
        <Link
          to="/borrower/upload"
          className={cn(s.navItem, { [s.active]: pathname === "/borrower/upload" })}
          title={collapsed ? "Upload Files" : undefined}
        >
          <span className={s.navLabel}>{NAV_ICON.upload}{!collapsed && <span>Upload Files</span>}</span>
        </Link>
      )}
      {perms.manageAccess && (
        <Link
          to="/borrower/team"
          className={cn(s.navItem, { [s.active]: pathname === "/borrower/team" })}
          title={collapsed ? "Team Access" : undefined}
        >
          <span className={s.navLabel}>{NAV_ICON.team}{!collapsed && <span>Team Access</span>}</span>
        </Link>
      )}
    </div>
  );
}

function DealContext({ dealId, isAdmin, anonymous, activeSection }: {
  dealId: string;
  isAdmin: boolean;
  anonymous: boolean;
  activeSection: string | null;
}) {
  const [deal, setDeal] = useState<{ deal_name: string; deal_reference: string } | null>(null);

  // The name, and nothing else. The page loads the contract anyway and the API
  // client dedupes in-flight requests, so this costs one cache read.
  useEffect(() => {
    let cancelled = false;
    api.getDeal(dealId)
      .then((d) => { if (!cancelled) setDeal(d); })
      .catch(() => {/* the nav renders without a name rather than not at all */});
    return () => { cancelled = true; };
  }, [dealId]);

  const items = (isAdmin ? DEAL_NAV : anonymous ? ANON_DEAL_NAV : NON_ADMIN_DEAL_NAV).items;

  return (
    <div className={s.sectionDivider}>
      <div className={s.facilityBlock}>
        <div className={s.facilityName}>{deal?.deal_name ?? "Deal"}</div>
        {deal?.deal_reference && <div className={s.facilityMeta}>{deal.deal_reference}</div>}
      </div>

      {items.map((item) => (
        <Link
          key={item.id}
          to={`/deal/${dealId}?section=${item.id}`}
          className={cn(s.navItem, s.facilitySubItem, { [s.active]: activeSection === item.id })}
        >
          <span className={s.navLabel}><span>{item.label}</span></span>
        </Link>
      ))}
    </div>
  );
}

function FacilityContext({ facilityId, facilityName, borrower, bookId, isAdmin, anonymous, activeSection, covenantStatus }: {
  facilityId: string;
  facilityName: string;
  borrower: SidebarBorrower | null;
  bookId: string | null;
  isAdmin: boolean;
  anonymous: boolean;
  activeSection: string | null;
  covenantStatus: string | null | undefined;
}) {
  // A guest under an anonymous grant loses the sections whose payloads name the
  // originator with no anonymous form. Write access inside a section is each
  // section's `canManage`, not a nav decision.
  const items = (isAdmin ? FACILITY_NAV : anonymous ? ANON_FACILITY_NAV : NON_ADMIN_FACILITY_NAV).items;

  // Up-navigation lands on the borrower's home — its book (the workspace).
  const upHref = bookId ? `/books/${bookId}?tab=snapshot` : borrower ? `/borrowers/${borrower.id}?manage=1` : "/borrowers";

  return (
    <div className={s.sectionDivider}>
      {/* Breadcrumb up to the borrower workspace (admin only). */}
      {isAdmin && borrower && (
        <Link to={upHref} className={s.breadcrumb}>
          ‹ {borrower.legal_name}
        </Link>
      )}

      <div className={s.facilityBlock}>
        <div className={s.facilityName}>{facilityName}</div>
        {/* `legal_name` is already the alias under an anonymous grant, the
            server redacted it, so this line needs no branch. */}
        {!isAdmin && borrower && <div className={s.facilityMeta}>{borrower.legal_name}</div>}
      </div>

      {items.map((item) => {
        const isActive = activeSection === item.id;
        // Overview only: covenants are read on the contract, so the dot goes on
        // the one remaining section.
        const attention =
          item.id === "overview" &&
          (covenantStatus === "BREACHED" || covenantStatus === "WARNING");
        return (
          <Link
            key={item.id}
            to={item.ready ? `/facility/${facilityId}?section=${item.id}` : "#"}
            onClick={(e) => { if (!item.ready) e.preventDefault(); }}
            className={cn(s.navItem, s.facilitySubItem, {
              [s.active]: isActive,
              [s.disabled]: !item.ready,
            })}
          >
            <span className={s.navLabel}><span>{item.label}</span></span>
            {attention && (
              <span className={cn(s.dot, covenantStatus === "BREACHED" ? s.breach : s.warn)} />
            )}
          </Link>
        );
      })}
    </div>
  );
}

/**
 * Facilities of the borrower workspace (admin), grouped by Deal: a facility only
 * means something under its contract. Each group links to the contract and offers
 * adding a facility under it, which is how the new one gets its `deal_id` without
 * a picker. A facility whose Deal is not in the list still gets a group, headed
 * by its id, so no facility is hidden.
 */

function FacilitiesList({ facilities, deals, borrowerId }: {
  facilities: SidebarFacility[];
  deals: SidebarDeal[];
  borrowerId: string;
}) {
  const byDeal = new Map<string, SidebarFacility[]>();
  for (const f of facilities) {
    const list = byDeal.get(f.deal_id) ?? [];
    list.push(f);
    byDeal.set(f.deal_id, list);
  }
  const dealsById = new Map(deals.map((d) => [d.id, d]));

  return (
    <CollapsibleGroup label={`FACILITIES (${facilities.length})`} icon={SECTION_ICON.facilities} defaultOpen>
      {/* No covenant dot: `GET /facilities` carries no metrics; a dot would need
          the status from the `/facilities/summary` store. */}
      {[...byDeal.entries()].map(([dealId, dealFacilities]) => {
        const deal = dealsById.get(dealId);
        return (
          <div key={dealId} className={s.dealGroup}>
            <Link to={`/deal/${dealId}`} className={s.dealGroupHead}>
              {deal?.deal_name ?? deal?.deal_reference ?? `Deal ${dealId.slice(0, 8)}`}
            </Link>
            {dealFacilities.map((d) => (
              <Link key={d.id} to={`/facility/${d.id}`} className={s.navItem}>
                <span className={s.navLabel}><span>{d.facility_name}</span></span>
              </Link>
            ))}
            <Link to={`/facility/new?deal=${dealId}`} className={s.navItem}>
              <span className={s.navLabel}><span>+ Add facility</span></span>
            </Link>
          </div>
        );
      })}
      <Link to={`/deal/new?borrower=${borrowerId}`} className={s.navItem}>
        <span className={s.navLabel}><span>+ New deal</span></span>
      </Link>
    </CollapsibleGroup>
  );
}

function matchDealId(pathname: string): string | null {
  const m = pathname.match(/^\/deal\/([^/]+)/);
  const id = m?.[1];
  return id && id !== "new" ? id : null;
}

function matchFacilityId(pathname: string): string | null {
  const m = pathname.match(/^\/facility\/([^/]+)/);
  const id = m?.[1];
  return id && id !== "new" ? id : null;
}

function matchBorrowerId(pathname: string): string | null {
  return pathname.match(/^\/borrowers\/([^/?]+)/)?.[1] ?? null;
}

function matchBookId(pathname: string): string | null {
  return pathname.match(/^\/books\/([^/?]+)/)?.[1] ?? null;
}

function buildBorrowerCtx({
  sbBorrower,
  sbBooks,
  sbFacilities,
  facilityId,
  facility,
  urlBorrowerId,
}: {
  sbBorrower: ReturnType<typeof useBorrowerStore.getState>["borrower"];
  sbBooks: ReturnType<typeof useBorrowerStore.getState>["books"];
  sbFacilities: ReturnType<typeof useBorrowerStore.getState>["facilities"];
  facilityId: string | null;
  facility: FacilityInfo | null;
  urlBorrowerId: string | null;
}): BorrowerCtx | null {
  // Trust the store only when it matches the URL's borrower (avoids stale leak).
  const storeMatchesUrl = !urlBorrowerId || sbBorrower?.id === urlBorrowerId;
  if (sbBorrower && storeMatchesUrl) {
    return {
      borrower: sbBorrower,
      activeBook: (sbBooks[0] ?? null) as BorrowerCtx["activeBook"],
      facilities: sbFacilities as BorrowerCtx["facilities"],
    };
  }
  // Partial context while the borrower store loads — only facility data available.
  if (facilityId && facility?.borrower) {
    return {
      borrower: {
        id: facility.borrower.id,
        legal_name: facility.borrower.legal_name,
        country: facility.borrower.country,
        // The chip states the borrower's operating currency: that is the book's,
        // not the facility's, which is only a reporting preference.
        currency: facility.book_currency || facility.currency,
        kyc_status: facility.borrower.kyc_status,
      } as SidebarBorrower,
      activeBook: null,
      facilities: [],
    };
  }
  return null;
}
